// Pure-DOM helper: fires a five-star burst from the given element.
// Used both by the PinButton component (bare-star list rows) and by
// the header pill buttons on the supplier/brief detail pages, which
// keep their bespoke pill layout and just opt into the animation.
//
// The host element must be a positioning context (`position: relative`
// in CSS) so the absolute particles anchor to it; PinButton's
// `.pin-toggle` class sets that, and the header pill call sites wrap
// their star icon in a `<span className="relative inline-flex">` so the
// particles burst from the star itself rather than the centre of the
// whole pill.
//
// prefers-reduced-motion users get nothing - no particles, no DOM
// churn - matching the existing ConfettiBurst behaviour.

import './pinBurst.css';

// Same 5-point star polygon as the source pin icon, so the particles
// read as miniature versions of what just got pinned.
const STAR_POINTS = '12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2';

const PARTICLE_COUNT = 5;
const PARTICLE_LIFE_MS = 650;

const prefersReducedMotion = () =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function firePinBurst(sourceEl) {
  if (!sourceEl || prefersReducedMotion()) return;
  // Evenly spaced 360° spread (72° between particles) with ±10° jitter
  // so the burst doesn't read as a stiff geometric shape. -90° starts
  // the first particle straight up.
  const baseAngle = -90;
  const angleStep = 360 / PARTICLE_COUNT;
  for (let i = 0; i < PARTICLE_COUNT; i++) {
    const angle = baseAngle + (i * angleStep) + (Math.random() * 20 - 10);
    const distance = 40 + Math.random() * 20; // 40-60px
    const rad = angle * (Math.PI / 180);
    const x = Math.cos(rad) * distance;
    const y = Math.sin(rad) * distance;
    const particle = document.createElement('span');
    particle.className = 'pin-burst-particle';
    particle.setAttribute('aria-hidden', 'true');
    particle.innerHTML = `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="${STAR_POINTS}"/></svg>`;
    particle.style.setProperty('--burst-x', `${x}px`);
    particle.style.setProperty('--burst-y', `${y}px`);
    sourceEl.appendChild(particle);
    // Defer the .bursting class so the browser commits the initial
    // (opacity:0, scale:0.2) state before the keyframe kicks in.
    requestAnimationFrame(() => particle.classList.add('bursting'));
    setTimeout(() => particle.remove(), PARTICLE_LIFE_MS);
  }
}

export const PIN_PULSE_MS = 400;
